import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  Building2,
  User,
  Phone,
  Mail,
  Calendar,
  CheckCircle2,
  AlertCircle,
  X,
  Save,
  Clock,
  Flame,
  Thermometer,
  Wrench,
  ChevronRight,
  ChevronDown,
  FileText,
  Lock
} from 'lucide-react';
import {
  fetchLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  getCurrentUserCompany,
  fetchVatEntities,
  fetchCompanyUsers,
  supabase,
  getCurrentUserRole,
  isMagicAdmin,
  type Location,
  type Company,
  type UserProfile,
  type VatEntity
} from '../lib/supabase';

export function LocationManagement() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [vatEntities, setVatEntities] = useState<VatEntity[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserProfile[]>([]);
  const [isMagicAdminUser, setIsMagicAdminUser] = useState(false);
  const [useExistingUser, setUseExistingUser] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    type: 'office' as Location['type'],
    vat_entity_id: '',
    property_manager_id: '',
    contact_person_name: '',
    contact_person_phone: '',
    contact_person_email: '',
    contact_person_role: '',
    communication_preferences: {
      email: true,
      sms: false,
      phone: false
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get current company
      const currentCompany = await getCurrentUserCompany();
      if (!currentCompany) {
        throw new Error('No company found');
      }
      setCompany(currentCompany);

      // Get current user role
      await loadCurrentUserRole();

      // Check if user is magic admin
      const magicAdmin = await isMagicAdmin();
      setIsMagicAdminUser(magicAdmin);

      // Load locations, VAT entities, and company users for this company
      const [locationsData, vatEntitiesData, usersData] = await Promise.all([
        fetchLocations(currentCompany.id),
        fetchVatEntities(currentCompany.id),
        fetchCompanyUsers(currentCompany.id)
      ]);
      
      setLocations(locationsData);
      setVatEntities(vatEntitiesData);
      
      // Filter out magic admin users
      const filteredUsers = usersData.filter(user => 
        !isMagicAdminEmail(user.email)
      );
      setCompanyUsers(filteredUsers);
      
      console.log("Loaded data:", {
        locations: locationsData.length,
        vatEntities: vatEntitiesData.length,
        users: filteredUsers.length
      });
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load locations');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role:roles(name)')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        return;
      }

      if (data && data.role) {
        setCurrentUserRole(data.role.name);
      }
    } catch (err) {
      console.error('Error getting current user role:', err);
    }
  };

  // Check if user is magic admin (gael@jaimy.be or jan@jaimy.be)
  const isMagicAdminEmail = (email: string) => {
    return email === 'gael@jaimy.be' || email === 'jan@jaimy.be';
  };

  const handleCreateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    try {
      setIsLoading(true);
      setError(null);

      // Prepare location data based on form inputs
      const locationData: any = {
        name: formData.name,
        address: formData.address,
        type: formData.type,
        company_id: company.id,
        status: 'active',
        last_maintenance: null,
        next_maintenance: null,
        maintenance_contract_type: null,
        maintenance_contract_frequency: null,
        maintenance_contract_price: null,
        maintenance_contract_active: false,
        booking_invite_pending: false,
        communication_preferences: formData.communication_preferences
      };

      // Add VAT entity if selected
      if (formData.vat_entity_id) {
        locationData.vat_entity_id = formData.vat_entity_id;
      }

      // Add property manager based on selection mode
      if (useExistingUser) {
        if (formData.property_manager_id) {
          locationData.property_manager_id = formData.property_manager_id;
          
          // Get selected user details for contact info
          const selectedUser = companyUsers.find(user => user.id === formData.property_manager_id);
          if (selectedUser) {
            locationData.contact_person_name = `${selectedUser.first_name} ${selectedUser.last_name}`;
            locationData.contact_person_email = selectedUser.email;
            locationData.contact_person_role = selectedUser.department;
            // Phone might not be available in user profile, so we'll use a placeholder
            locationData.contact_person_phone = '+32 123 456 789';
          }
        }
      } else {
        // Use manually entered contact details
        locationData.contact_person_name = formData.contact_person_name;
        locationData.contact_person_phone = formData.contact_person_phone;
        locationData.contact_person_email = formData.contact_person_email;
        locationData.contact_person_role = formData.contact_person_role;
      }

      const newLocation = await createLocation(locationData);

      setLocations(prev => [...prev, newLocation]);
      setIsCreateModalOpen(false);
      resetForm();
    } catch (err) {
      console.error('Error creating location:', err);
      setError('Failed to create location');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) return;

    try {
      setIsLoading(true);
      setError(null);

      // Prepare location data based on form inputs
      const locationData: any = {
        name: formData.name,
        address: formData.address,
        type: formData.type,
        communication_preferences: formData.communication_preferences
      };

      // Add VAT entity if selected
      if (formData.vat_entity_id) {
        locationData.vat_entity_id = formData.vat_entity_id;
      }

      // Add property manager based on selection mode
      if (useExistingUser) {
        if (formData.property_manager_id) {
          locationData.property_manager_id = formData.property_manager_id;
          
          // Get selected user details for contact info
          const selectedUser = companyUsers.find(user => user.id === formData.property_manager_id);
          if (selectedUser) {
            locationData.contact_person_name = `${selectedUser.first_name} ${selectedUser.last_name}`;
            locationData.contact_person_email = selectedUser.email;
            locationData.contact_person_role = selectedUser.department;
            // Phone might not be available in user profile, so we'll keep existing or use placeholder
            locationData.contact_person_phone = selectedLocation.contact_person_phone || '+32 123 456 789';
          }
        }
      } else {
        // Use manually entered contact details
        locationData.contact_person_name = formData.contact_person_name;
        locationData.contact_person_phone = formData.contact_person_phone;
        locationData.contact_person_email = formData.contact_person_email;
        locationData.contact_person_role = formData.contact_person_role;
        // Clear property manager ID if using manual entry
        locationData.property_manager_id = null;
      }

      const updatedLocation = await updateLocation(selectedLocation.id, locationData);

      setLocations(prev => prev.map(loc => loc.id === selectedLocation.id ? updatedLocation : loc));
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Error updating location:', err);
      setError('Failed to update location');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!selectedLocation) return;

    try {
      setIsLoading(true);
      setError(null);

      // Check if user has permission to delete
      if (currentUserRole !== 'admin' && !isMagicAdminUser) {
        setError('Only administrators can delete locations');
        setIsLoading(false);
        return;
      }

      await deleteLocation(selectedLocation.id);
      setLocations(prev => prev.filter(loc => loc.id !== selectedLocation.id));
      setIsDeleteModalOpen(false);
      setSelectedLocation(null);
    } catch (err) {
      console.error('Error deleting location:', err);
      setError('Failed to delete location');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      type: 'office',
      vat_entity_id: '',
      property_manager_id: '',
      contact_person_name: '',
      contact_person_phone: '',
      contact_person_email: '',
      contact_person_role: '',
      communication_preferences: {
        email: true,
        sms: false,
        phone: false
      }
    });
    setUseExistingUser(true);
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getLocationTypeIcon = (type: Location['type']) => {
    switch (type) {
      case 'office':
        return Building2;
      case 'retail':
        return Building2;
      case 'warehouse':
        return Building2;
      case 'production':
        return Building2;
      case 'residential':
        return Building2;
      case 'coliving':
        return Building2;
      default:
        return Building2;
    }
  };

  const getMaintenanceTypeIcon = (type: Location['maintenance_contract_type']) => {
    switch (type) {
      case 'gas':
        return Flame;
      case 'mazout':
        return Thermometer;
      case 'heat_pump':
        return Wrench;
      default:
        return null;
    }
  };

  const getMaintenanceStatus = (location: Location) => {
    if (!location.next_maintenance) return { status: 'unknown', color: 'text-gray-500' };
    
    const nextDate = new Date(location.next_maintenance);
    const today = new Date();
    const daysUntil = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return { status: 'overdue', color: 'text-red-600' };
    } else if (daysUntil <= 30) {
      return { status: 'due_soon', color: 'text-orange-600' };
    } else {
      return { status: 'scheduled', color: 'text-green-600' };
    }
  };

  if (isLoading && locations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-600 mt-1">Manage your properties and locations</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsCreateModalOpen(true);
            }}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Plus className="h-5 w-5" />
            <span>Add Location</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredLocations.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow-sm p-12 text-center">
            <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Locations Found</h3>
            <p className="text-gray-600 mb-6">
              {locations.length === 0 
                ? 'Get started by adding your first location.'
                : 'No locations match your current search criteria.'
              }
            </p>
            {locations.length === 0 && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="gradient-bg text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                Add First Location
              </button>
            )}
          </div>
        ) : (
          filteredLocations.map((location) => {
            const LocationTypeIcon = getLocationTypeIcon(location.type);
            const MaintenanceTypeIcon = location.maintenance_contract_type 
              ? getMaintenanceTypeIcon(location.maintenance_contract_type)
              : null;
            const maintenanceStatus = getMaintenanceStatus(location);
            const isExpanded = expandedLocation === location.id;
            
            return (
              <div 
                key={location.id} 
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-jaimy-red transition-colors overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <LocationTypeIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{location.name}</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                          <MapPin className="h-4 w-4" />
                          {location.address}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                            {location.type.replace('_', ' ')}
                          </span>
                          {location.status === 'active' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {location.status.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedLocation(location);
                          
                          // Determine if we should use existing user or manual entry
                          const useExisting = !!location.property_manager_id;
                          setUseExistingUser(useExisting);
                          
                          setFormData({
                            name: location.name,
                            address: location.address,
                            type: location.type,
                            vat_entity_id: location.vat_entity_id || '',
                            property_manager_id: location.property_manager_id || '',
                            contact_person_name: location.contact_person_name,
                            contact_person_phone: location.contact_person_phone,
                            contact_person_email: location.contact_person_email,
                            contact_person_role: location.contact_person_role,
                            communication_preferences: location.communication_preferences
                          });
                          setIsEditModalOpen(true);
                        }}
                        className="text-gray-400 hover:text-jaimy-red transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setExpandedLocation(isExpanded ? null : location.id);
                        }}
                        className="text-gray-400 hover:text-jaimy-red transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="mt-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span>{location.contact_person_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${location.contact_person_email}`} className="hover:text-jaimy-red">
                        {location.contact_person_email}
                      </a>
                    </div>
                  </div>

                  {/* Maintenance Contract Info (if exists) */}
                  {location.maintenance_contract_active && MaintenanceTypeIcon && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MaintenanceTypeIcon className="h-4 w-4 text-orange-600" />
                          <span className="text-sm font-medium">
                            HeatingComfort Contract
                          </span>
                        </div>
                        <span className={`text-sm ${maintenanceStatus.color}`}>
                          {location.next_maintenance 
                            ? new Date(location.next_maintenance).toLocaleDateString() 
                            : 'Not scheduled'
                          }
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        See details in HeatingComfort Pro section
                      </p>
                    </div>
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Location Timeline</h4>
                    
                    <div className="space-y-4">
                      {/* Mock timeline entries */}
                      <div className="relative pl-6">
                        <div className="absolute left-0 top-2 h-2 w-2 rounded-full bg-blue-500"></div>
                        <div className="border-l-2 border-gray-200 pl-4 pb-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Today</p>
                              <p className="text-gray-900 text-sm">Location details updated</p>
                            </div>
                            <div className="text-xs text-gray-500">
                              System
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {location.maintenance_contract_active && location.last_maintenance && (
                        <div className="relative pl-6">
                          <div className="absolute left-0 top-2 h-2 w-2 rounded-full bg-green-500"></div>
                          <div className="border-l-2 border-gray-200 pl-4 pb-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-sm text-gray-600">{new Date(location.last_maintenance).toLocaleDateString()}</p>
                                <p className="text-gray-900 text-sm">Maintenance performed</p>
                                <p className="text-xs text-gray-600 mt-1">
                                  Regular maintenance of {location.maintenance_contract_type} system
                                </p>
                              </div>
                              <div className="text-xs text-gray-500">
                                Technician
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="relative pl-6">
                        <div className="absolute left-0 top-2 h-2 w-2 rounded-full bg-gray-500"></div>
                        <div className="border-l-2 border-gray-200 pl-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm text-gray-600">{new Date(location.created_at).toLocaleDateString()}</p>
                              <p className="text-gray-900 text-sm">Location created</p>
                            </div>
                            <div className="text-xs text-gray-500">
                              System
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex justify-end">
                      {currentUserRole === 'admin' || isMagicAdminUser ? (
                        <button
                          onClick={() => {
                            setSelectedLocation(location);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Location
                        </button>
                      ) : (
                        <div className="text-gray-400 text-sm flex items-center gap-1">
                          <Lock className="h-4 w-4" />
                          <span>Only admins can delete locations</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create Location Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Add New Location</h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateLocation} className="p-6 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Location Name *
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Address *
                </label>
                <input
                  type="text"
                  id="address"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Location Type *
                </label>
                <select
                  id="type"
                  required
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as Location['type'] }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                >
                  <option value="office">Office</option>
                  <option value="retail">Retail</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="production">Production</option>
                  <option value="residential">Residential</option>
                  <option value="coliving">Co-living</option>
                </select>
              </div>
              
              {/* VAT Entity Selection */}
              {vatEntities.length > 0 && (
                <div>
                  <label htmlFor="vat_entity" className="block text-sm font-medium text-gray-700">
                    VAT Entity
                  </label>
                  <select
                    id="vat_entity"
                    value={formData.vat_entity_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, vat_entity_id: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="">Select VAT Entity</option>
                    {vatEntities.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.name} {entity.is_default ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Property Manager Section */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Property Manager</h3>
                
                {/* Toggle between existing user and manual entry */}
                <div className="flex items-center mb-4">
                  <div className="flex items-center">
                    <button
                      type="button"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        useExistingUser ? 'bg-jaimy-red' : 'bg-gray-200'
                      }`}
                      onClick={() => setUseExistingUser(true)}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          useExistingUser ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Select existing user
                    </span>
                  </div>
                  
                  <div className="flex items-center ml-6">
                    <button
                      type="button"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        !useExistingUser ? 'bg-jaimy-red' : 'bg-gray-200'
                      }`}
                      onClick={() => setUseExistingUser(false)}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          !useExistingUser ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Enter manually
                    </span>
                  </div>
                </div>
                
                {useExistingUser ? (
                  /* Existing User Selection */
                  <div>
                    <select
                      value={formData.property_manager_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, property_manager_id: e.target.value }))}
                      className="block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="">Select a user</option>
                      {companyUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      This user will be assigned as the property manager for this location
                    </p>
                  </div>
                ) : (
                  /* Manual Contact Person Entry */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="contact_name"
                        required={!useExistingUser}
                        value={formData.contact_person_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_name: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_role" className="block text-sm font-medium text-gray-700">
                        Role *
                      </label>
                      <input
                        type="text"
                        id="contact_role"
                        required={!useExistingUser}
                        value={formData.contact_person_role}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_role: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="contact_email"
                        required={!useExistingUser}
                        value={formData.contact_person_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_email: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        id="contact_phone"
                        required={!useExistingUser}
                        value={formData.contact_person_phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_phone: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Communication Preferences</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pref_email"
                      checked={formData.communication_preferences.email}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        communication_preferences: {
                          ...prev.communication_preferences,
                          email: e.target.checked
                        }
                      }))}
                      className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                    />
                    <label htmlFor="pref_email" className="ml-2 text-sm text-gray-700">
                      Email notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pref_sms"
                      checked={formData.communication_preferences.sms}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        communication_preferences: {
                          ...prev.communication_preferences,
                          sms: e.target.checked
                        }
                      }))}
                      className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                    />
                    <label htmlFor="pref_sms" className="ml-2 text-sm text-gray-700">
                      SMS notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pref_phone"
                      checked={formData.communication_preferences.phone}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        communication_preferences: {
                          ...prev.communication_preferences,
                          phone: e.target.checked
                        }
                      }))}
                      className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                    />
                    <label htmlFor="pref_phone" className="ml-2 text-sm text-gray-700">
                      Phone notifications
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Create Location
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Location Modal */}
      {isEditModalOpen && selectedLocation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit Location</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateLocation} className="p-6 space-y-4">
              <div>
                <label htmlFor="edit_name" className="block text-sm font-medium text-gray-700">
                  Location Name *
                </label>
                <input
                  type="text"
                  id="edit_name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="edit_address" className="block text-sm font-medium text-gray-700">
                  Address *
                </label>
                <input
                  type="text"
                  id="edit_address"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="edit_type" className="block text-sm font-medium text-gray-700">
                  Location Type *
                </label>
                <select
                  id="edit_type"
                  required
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as Location['type'] }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                >
                  <option value="office">Office</option>
                  <option value="retail">Retail</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="production">Production</option>
                  <option value="residential">Residential</option>
                  <option value="coliving">Co-living</option>
                </select>
              </div>
              
              {/* VAT Entity Selection */}
              {vatEntities.length > 0 && (
                <div>
                  <label htmlFor="edit_vat_entity" className="block text-sm font-medium text-gray-700">
                    VAT Entity
                  </label>
                  <select
                    id="edit_vat_entity"
                    value={formData.vat_entity_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, vat_entity_id: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="">Select VAT Entity</option>
                    {vatEntities.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.name} {entity.is_default ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Property Manager Section */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Property Manager</h3>
                
                {/* Toggle between existing user and manual entry */}
                <div className="flex items-center mb-4">
                  <div className="flex items-center">
                    <button
                      type="button"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        useExistingUser ? 'bg-jaimy-red' : 'bg-gray-200'
                      }`}
                      onClick={() => setUseExistingUser(true)}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          useExistingUser ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Select existing user
                    </span>
                  </div>
                  
                  <div className="flex items-center ml-6">
                    <button
                      type="button"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        !useExistingUser ? 'bg-jaimy-red' : 'bg-gray-200'
                      }`}
                      onClick={() => setUseExistingUser(false)}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          !useExistingUser ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Enter manually
                    </span>
                  </div>
                </div>
                
                {useExistingUser ? (
                  /* Existing User Selection */
                  <div>
                    <select
                      value={formData.property_manager_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, property_manager_id: e.target.value }))}
                      className="block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="">Select a user</option>
                      {companyUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      This user will be assigned as the property manager for this location
                    </p>
                  </div>
                ) : (
                  /* Manual Contact Person Entry */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="edit_contact_name" className="block text-sm font-medium text-gray-700">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="edit_contact_name"
                        required={!useExistingUser}
                        value={formData.contact_person_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_name: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit_contact_role" className="block text-sm font-medium text-gray-700">
                        Role *
                      </label>
                      <input
                        type="text"
                        id="edit_contact_role"
                        required={!useExistingUser}
                        value={formData.contact_person_role}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_role: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit_contact_email" className="block text-sm font-medium text-gray-700">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="edit_contact_email"
                        required={!useExistingUser}
                        value={formData.contact_person_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_email: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit_contact_phone" className="block text-sm font-medium text-gray-700">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        id="edit_contact_phone"
                        required={!useExistingUser}
                        value={formData.contact_person_phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_person_phone: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Communication Preferences</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_pref_email"
                      checked={formData.communication_preferences.email}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        communication_preferences: {
                          ...prev.communication_preferences,
                          email: e.target.checked
                        }
                      }))}
                      className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                    />
                    <label htmlFor="edit_pref_email" className="ml-2 text-sm text-gray-700">
                      Email notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_pref_sms"
                      checked={formData.communication_preferences.sms}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        communication_preferences: {
                          ...prev.communication_preferences,
                          sms: e.target.checked
                        }
                      }))}
                      className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                    />
                    <label htmlFor="edit_pref_sms" className="ml-2 text-sm text-gray-700">
                      SMS notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_pref_phone"
                      checked={formData.communication_preferences.phone}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        communication_preferences: {
                          ...prev.communication_preferences,
                          phone: e.target.checked
                        }
                      }))}
                      className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                    />
                    <label htmlFor="edit_pref_phone" className="ml-2 text-sm text-gray-700">
                      Phone notifications
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Update Location
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedLocation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Location</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <span className="font-medium">{selectedLocation.name}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteLocation}
                  disabled={isLoading || currentUserRole !== 'admin' && !isMagicAdminUser}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete Location
                    </>
                  )}
                </button>
              </div>
              {currentUserRole !== 'admin' && !isMagicAdminUser && (
                <p className="mt-4 text-sm text-red-500 flex items-center justify-center gap-1">
                  <Lock className="h-4 w-4" />
                  Only administrators can delete locations
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}